import React, { useState } from 'react';
import { Auth } from 'aws-amplify';
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import { GamifyToast } from '../../common/CustomToasts';

const API_BASE = 'https://api.gogamify.com/usermgmt';

const getToken = async () => {
  const session = await Auth.currentSession();
  return session.getIdToken().getJwtToken();
};

const apiRequest = async (method, path, body = null) => {
  const token = await getToken();
  const options = {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  };
  if (body) options.body = JSON.stringify(body);
  const res = await fetch(`${API_BASE}${path}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const errorMsg =
      data.error || data.message || `Request failed (${res.status})`;
    const err = new Error(errorMsg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
};

export default function UserStatus() {
  const [email, setEmail] = useState('');
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);

  const lookupUser = async () => {
    if (!email.trim()) return;
    setLoading(true);
    setError(null);
    setUserData(null);
    try {
      const data = await apiRequest(
        'GET',
        `/user?email=${encodeURIComponent(email.trim())}`
      );
      setUserData(data);
    } catch (err) {
      if (err.status === 403) {
        setError('Not authorized. You must be in the paiv_admin_rw group.');
      } else if (err.status === 400) {
        setError('Invalid input. Please check the email address.');
      } else {
        setError(err.message || 'An unexpected error occurred.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (action) => {
    setActionLoading(action);
    try {
      if (action === 'enable') {
        await apiRequest('POST', '/user/enable', { email: email.trim() });
        GamifyToast.success('User Enabled', 'User account has been restored.');
      } else if (action === 'delete') {
        await apiRequest('POST', '/user/delete', { email: email.trim() });
        GamifyToast.success(
          'User Deleted',
          'User has been permanently removed from all systems.'
        );
        setUserData(null);
        setActionLoading(null);
        return;
      } else if (action === 'reset-password') {
        await apiRequest('POST', '/user/reset-password', {
          email: email.trim(),
        });
        GamifyToast.success(
          'Password Reset Sent',
          'A password reset email has been sent to the user.'
        );
      } else if (action === 'resend-welcome') {
        await apiRequest('POST', '/user/resend-welcome', {
          email: email.trim(),
        });
        GamifyToast.success(
          'Welcome Email Resent',
          'The welcome/invite email has been resent.'
        );
      }
      // Re-fetch user status after action
      const data = await apiRequest(
        'GET',
        `/user?email=${encodeURIComponent(email.trim())}`
      );
      setUserData(data);
    } catch (err) {
      if (err.status === 409 && err.data?.suggestion) {
        GamifyToast.error('Action Failed', err.data.suggestion);
      } else if (err.status === 404) {
        GamifyToast.error('Not Found', err.message);
      } else {
        GamifyToast.error('Error', err.message || 'Action failed.');
      }
    } finally {
      setActionLoading(null);
    }
  };

  const openConfirm = (action, message, doubleConfirm = false) => {
    setConfirmDialog({ action, message, doubleConfirm, step: 1 });
  };

  const handleConfirm = () => {
    const { action, doubleConfirm, step } = confirmDialog;
    if (doubleConfirm && step === 1) {
      setConfirmDialog({
        ...confirmDialog,
        step: 2,
        message:
          'This is IRREVERSIBLE. The user will be permanently deleted from Cognito, DynamoDB, and all org memberships. Are you absolutely sure?',
      });
      return;
    }
    setConfirmDialog(null);
    handleAction(action);
  };

  const cognitoStatus = userData?.cognito?.userStatus;
  const isDisabledOrDeleted =
    userData?.cognito?.enabled === false ||
    userData?.ddbUser?.isDeleted === true ||
    userData?.orgMemberships?.some((m) => m.isDeleted || !m.isActive);

  const showResetPassword = userData?.cognito !== null;
  const showResendWelcome = cognitoStatus === 'FORCE_CHANGE_PASSWORD';

  return (
    <Box sx={{ maxWidth: 900, margin: '0 auto', padding: '20px 0' }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
        User Status Lookup
      </Typography>

      {/* Search Section */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
        <TextField
          label="User Email"
          variant="outlined"
          size="small"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && lookupUser()}
          sx={{ flex: 1 }}
          placeholder="john@example.com"
        />
        <Button
          variant="contained"
          onClick={lookupUser}
          disabled={loading || !email.trim()}
          sx={{
            backgroundColor: '#FF6A00',
            '&:hover': { backgroundColor: '#DC5C01' },
          }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : 'Look Up'}
        </Button>
      </Box>

      {/* Quick reference card */}
      <Box
        sx={{
          mb: 3,
          p: 1.5,
          borderRadius: 1,
          backgroundColor: 'rgba(0,0,0,0.03)',
          border: '1px solid rgba(0,0,0,0.08)',
        }}
      >
        <Typography
          variant="caption"
          sx={{ color: '#666', lineHeight: 1.6, display: 'block' }}
        >
          <strong>Fully enabled user:</strong> Cognito enabled + status
          CONFIRMED or RESET_REQUIRED, DDB isDeleted=false &amp; status=active,
          OrgMembership isActive=true &amp; isDeleted=false &amp; status=active.
        </Typography>
        <Typography
          variant="caption"
          sx={{ color: '#888', lineHeight: 1.6, display: 'block', mt: 0.5 }}
        >
          <strong>Cognito statuses:</strong> CONFIRMED = normal active user ·
          FORCE_CHANGE_PASSWORD = invited but hasn&apos;t set password yet ·
          RESET_REQUIRED = must reset password on next login · DISABLED =
          account manually disabled.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {userData && (
        <>
          {/* Cognito Status */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Cognito Status
            </Typography>
            {userData.cognito ? (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}
              >
                <InfoRow
                  label="Exists in Cognito"
                  value={<Chip label="Yes" color="success" size="small" />}
                />
                <InfoRow
                  label="User Status"
                  value={
                    <Chip
                      label={userData.cognito.userStatus}
                      color={
                        userData.cognito.userStatus === 'CONFIRMED'
                          ? 'success'
                          : 'warning'
                      }
                      size="small"
                    />
                  }
                />
                <InfoRow
                  label="Enabled"
                  value={
                    <Chip
                      label={userData.cognito.enabled ? 'Yes' : 'No'}
                      color={userData.cognito.enabled ? 'success' : 'error'}
                      size="small"
                    />
                  }
                />
                <InfoRow
                  label="Email"
                  value={userData.cognito.attributes?.email || '—'}
                />
                <InfoRow
                  label="Name"
                  value={userData.cognito.attributes?.name || '—'}
                />
                <InfoRow
                  label="Phone"
                  value={userData.cognito.attributes?.phone_number || '—'}
                />
                <InfoRow
                  label="Created"
                  value={formatDate(userData.cognito.userCreateDate)}
                />
                <InfoRow
                  label="Last Modified"
                  value={formatDate(userData.cognito.userLastModifiedDate)}
                />
                {userData.cognitoGroups?.length > 0 && (
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, mb: 0.5 }}
                    >
                      Groups:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {userData.cognitoGroups.map((g, i) => (
                        <Chip key={i} label={g.groupName} size="small" />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="warning">Not found in Cognito</Alert>
            )}
          </Paper>

          {/* DynamoDB User Record */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              DynamoDB User Record
            </Typography>
            {userData.ddbUser ? (
              <Box
                sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}
              >
                <InfoRow
                  label="ID"
                  value={<CopyableText text={userData.ddbUser.id} />}
                />
                <InfoRow label="Email" value={userData.ddbUser.email} />
                <InfoRow label="Name" value={userData.ddbUser.name || '—'} />
                <InfoRow
                  label="Status"
                  value={
                    <HighlightedValue value={userData.ddbUser.status || '—'} />
                  }
                />
                <InfoRow
                  label="Is Deleted"
                  value={
                    <Chip
                      label={userData.ddbUser.isDeleted ? 'Yes' : 'No'}
                      color={userData.ddbUser.isDeleted ? 'error' : 'default'}
                      size="small"
                    />
                  }
                />
                <InfoRow
                  label="Initials"
                  value={userData.ddbUser.initials || '—'}
                />
                <InfoRow label="Phone" value={userData.ddbUser.phone || '—'} />
              </Box>
            ) : (
              <Alert severity="info">No DynamoDB user record found</Alert>
            )}
          </Paper>

          {/* Org Memberships */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Org Memberships
            </Typography>
            {userData.orgMemberships?.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Org ID</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Is Active</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Is Deleted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {userData.orgMemberships.map((m, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          backgroundColor:
                            m.isDeleted || !m.isActive
                              ? 'rgba(227, 80, 80, 0.08)'
                              : 'inherit',
                        }}
                      >
                        <TableCell>
                          <CopyableText text={m.orgID} />
                        </TableCell>
                        <TableCell>{m.status || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            label={m.isActive ? 'Yes' : 'No'}
                            color={m.isActive ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={m.isDeleted ? 'Yes' : 'No'}
                            color={m.isDeleted ? 'error' : 'default'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">No org memberships found</Alert>
            )}
          </Paper>

          {/* Action Buttons */}
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5 }}>
              Actions
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {isDisabledOrDeleted && (
                <Button
                  variant="contained"
                  color="success"
                  disabled={!!actionLoading}
                  onClick={() =>
                    openConfirm(
                      'enable',
                      'This will enable the Cognito account, clear deleted flags, and restore org memberships. Proceed?'
                    )
                  }
                >
                  {actionLoading === 'enable' ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    'Enable User'
                  )}
                </Button>
              )}

              {showResetPassword && (
                <Button
                  variant="outlined"
                  disabled={!!actionLoading}
                  onClick={() =>
                    openConfirm(
                      'reset-password',
                      'This will send a password reset email to the user. Proceed?'
                    )
                  }
                >
                  {actionLoading === 'reset-password' ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Reset Password'
                  )}
                </Button>
              )}

              {showResendWelcome && (
                <Button
                  variant="outlined"
                  disabled={!!actionLoading}
                  onClick={() =>
                    openConfirm(
                      'resend-welcome',
                      'This will resend the welcome/invite email. Only works if the user has not yet confirmed. Proceed?'
                    )
                  }
                >
                  {actionLoading === 'resend-welcome' ? (
                    <CircularProgress size={20} />
                  ) : (
                    'Resend Welcome'
                  )}
                </Button>
              )}

              <Button
                variant="contained"
                color="error"
                disabled={!!actionLoading}
                onClick={() =>
                  openConfirm(
                    'delete',
                    'This will PERMANENTLY delete the user from Cognito, DynamoDB, and all org memberships. This cannot be undone. Are you sure?',
                    true
                  )
                }
              >
                {actionLoading === 'delete' ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  'Delete User'
                )}
              </Button>
            </Box>
          </Paper>
        </>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={!!confirmDialog}
        onClose={() => setConfirmDialog(null)}
        maxWidth="sm"
      >
        <DialogTitle>
          {confirmDialog?.action === 'delete'
            ? 'Confirm Deletion'
            : 'Confirm Action'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>{confirmDialog?.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog(null)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            color={confirmDialog?.action === 'delete' ? 'error' : 'primary'}
            variant="contained"
            autoFocus
          >
            {confirmDialog?.action === 'delete' && confirmDialog?.step === 2
              ? 'Yes, Delete Permanently'
              : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 120 }}>
        {label}:
      </Typography>
      <Typography variant="body2" component="span">
        {value}
      </Typography>
    </Box>
  );
}

function CopyableText({ text }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <span>{text}</span>
      <Tooltip title={copied ? 'Copied!' : 'Copy ID'}>
        <IconButton size="small" onClick={handleCopy} sx={{ p: 0.25 }}>
          <ContentCopy sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function HighlightedValue({ value }) {
  const isCorrupted =
    typeof value === 'string' && value.toLowerCase().includes('deleted');
  return (
    <Chip
      label={value}
      size="small"
      color={isCorrupted ? 'error' : 'default'}
      variant={isCorrupted ? 'filled' : 'outlined'}
    />
  );
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleString();
  } catch {
    return dateStr;
  }
}
